import { Camera, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { useToast } from "@/shared/components/ui/use-toast";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { Spinner } from "@/shared/components/ui/spinner";
import { useState } from "react";
import { useEmitAvatarUpdate } from "../events/avatarUpdateEvent";
import { post, del } from "@/shared/utils/fetchApi";

export const AvatarUpload = () => {
  const { t } = useTranslation();
  const { user, refreshSession } = useAuth();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  // URL do avatar recém-enviado. O proxy serve a imagem com cache de 1 ano e o
  // avatar é gravado sempre no mesmo path (upsert), então um re-upload com a mesma
  // extensão produz URL idêntica — o browser serve a imagem velha do cache e o
  // profile "não muda". Guardar localmente + cache-buster força o refetch na hora,
  // sem depender do timing do refreshSession.
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(null);
  // Após remover, força o fallback (iniciais) sem esperar o refreshSession zerar o
  // `user.avatarUrl`. Resetado no próximo upload.
  const [removed, setRemoved] = useState(false);
  const { emitAvatarUpdate } = useEmitAvatarUpdate();

  const handleAvatarUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      setIsUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      // Check file type
      if (!file.type.startsWith("image/")) {
        toast({
          variant: "destructive",
          title: t("profile.avatar.invalidFileType"),
          description: t("profile.avatar.invalidFileTypeDescription"),
        });
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: t("profile.avatar.fileTooLarge"),
          description: t("profile.avatar.fileTooLargeDescription"),
        });
        return;
      }

      // Verificar se user.id existe
      if (!user?.id) {
        throw new Error("ID do usuário não encontrado");
      }

      // Create FormData to send the file
      const formData = new FormData();
      formData.append("avatar", file, file.name);

      // POST /users/:id/avatar — resposta flat { avatarUrl, profile } (sem `success`).
      const data = await post<{ avatarUrl?: string; profile?: unknown }>(
        `/users/${user.id}/avatar`,
        formData,
        { parseJson: true, headers: {} },
      );
      const avatarUrl = data.avatarUrl ?? "";

      // Cache-buster: garante src novo mesmo que a URL gravada seja idêntica
      // (mesma extensão → mesmo path). Sem isso o browser reusa a imagem cacheada.
      const bustedUrl = avatarUrl
        ? `${avatarUrl}${avatarUrl.includes("?") ? "&" : "?"}t=${Date.now()}`
        : avatarUrl;
      setRemoved(false);
      setLocalAvatarUrl(bustedUrl);

      // Refresh session to update user metadata
      await refreshSession();

      // Emite evento de atualização de avatar para outros componentes
      emitAvatarUpdate(user.id, bustedUrl);

      toast({
        title: t("profile.avatar.updateSuccess"),
        description: t("profile.avatar.updateSuccessDescription"),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.avatar.updateErrorDescription"),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    try {
      if (!user?.id) {
        throw new Error("ID do usuário não encontrado");
      }
      setIsUploading(true);

      await del(`/users/${user.id}/avatar`);

      setLocalAvatarUrl(null);
      setRemoved(true);

      // Refresh session to update user metadata
      await refreshSession();

      // URL vazia → navbar/contexto caem no fallback (iniciais)
      emitAvatarUpdate(user.id, "");

      toast({
        title: t("profile.avatar.removeSuccess"),
        description: t("profile.avatar.removeSuccessDescription"),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("profile.avatar.removeErrorDescription"),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const displayAvatarUrl = removed
    ? ""
    : localAvatarUrl ||
      user?.avatarUrl ||
      user?.user_metadata?.avatar_url ||
      "";
  const hasAvatar = Boolean(displayAvatarUrl);

  return (
    <div className="flex justify-center">
      <div className="relative group">
        <Avatar className="h-24 w-24">
          <AvatarImage src={displayAvatarUrl} alt={user?.email || ""} />
          <AvatarFallback className="bg-primary/10">
            {user?.user_metadata?.full_name?.[0]?.toUpperCase() ||
              user?.email?.[0]?.toUpperCase() ||
              "U"}
          </AvatarFallback>
        </Avatar>
        <label
          htmlFor="avatar-upload"
          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity"
        >
          <Camera className="h-6 w-6 text-white" />
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarUpload}
            disabled={isUploading}
          />
        </label>
        {hasAvatar && !isUploading && (
          <button
            type="button"
            onClick={handleAvatarRemove}
            disabled={isUploading}
            aria-label={t("profile.avatar.remove")}
            title={t("profile.avatar.remove")}
            className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-md transition-colors hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-full">
            <Spinner className="text-white" />
          </div>
        )}
      </div>
    </div>
  );
};
